/**
 * Shared types for the optional, on-demand AI **flow review**.
 *
 * A review is an entirely-LLM judgment of a workflow ("flow") against a fixed
 * rubric. It produces a Lighthouse-style scorecard (an overall score plus a
 * per-category score) and a flat list of comments. The reviewer either:
 *   - creates a {@link https://… workflow proposal} carrying the scorecard +
 *     comments (the only durable surface), or
 *   - returns the scorecard + comments ephemerally (modal-only).
 *
 * These types are the single source of truth shared by the API (agent +
 * proposal column `$type`), the proposal serializer, and the web client.
 *
 * @module workflow-review
 */

/** Severity of a single review comment. `error` is reserved for the two hard
 *  auth rules (authenticated flow missing login / logged-out flow reusing a
 *  session). */
export type ReviewCommentSeverity = 'info' | 'warning' | 'error';

/** Stable category keys for the review rubric. Four coherent buckets:
 *  `data` folds isolation + provisioning; `setup` folds auth/preconditions +
 *  inputs/scenarios. */
export type ReviewCategoryKey = 'clarity' | 'assertions' | 'data' | 'setup';

/** Display metadata for a rubric category (key → human label + a short label
 *  for compact gauge rows + a one-line description used in tooltips). */
export interface ReviewCategoryMeta {
  key: ReviewCategoryKey;
  label: string;
  short: string;
  description: string;
}

/** Ordered rubric categories with their display labels. The agent scores each
 *  one; the UI renders gauges in this order. */
export const REVIEW_CATEGORIES: readonly ReviewCategoryMeta[] = [
  {
    key: 'clarity',
    label: 'Clarity & Coherence',
    short: 'Clarity',
    description:
      'One coherent journey: human-intent steps, a name and description that match the steps, and no redundant or out-of-order navigation.',
  },
  {
    key: 'assertions',
    label: 'Assertions & Verification',
    short: 'Assertions',
    description:
      'Verifies the key outcome with specific, observable checks that target the right thing at the right severity.',
  },
  {
    key: 'data',
    label: 'Data & Isolation',
    short: 'Data',
    description:
      'Fresh, non-colliding data at an isolation level that fits the blast radius — and provisions what it needs instead of assuming records already exist.',
  },
  {
    key: 'setup',
    label: 'Setup & Inputs',
    short: 'Setup',
    description:
      'Correct auth and preconditions, with declared inputs and scenarios that are actually used and stay a single journey.',
  },
] as const;

/** A single category's score (0–100). */
export interface ReviewCategoryScore {
  key: ReviewCategoryKey;
  /** 0–100, higher is better. */
  score: number;
}

/** The scorecard: an overall score plus per-category scores. */
export interface ReviewScorecard {
  /** 0–100 overall, the reviewer's roll-up. */
  overall: number;
  categories: ReviewCategoryScore[];
}

/** One actionable comment, optionally pinned to specific nodes. */
export interface ReviewComment {
  category: ReviewCategoryKey;
  severity: ReviewCommentSeverity;
  /** Node IDs this comment applies to (may be empty for flow-level comments). */
  nodeIds: string[];
  title: string;
  message: string;
  /** A concrete fix, when the reviewer can name one. */
  suggestion?: string;
}

/** Display band for a 0–100 score, mapped to the app's colorblind palette. */
export type ReviewScoreBand = 'good' | 'needs-work' | 'poor';

/** Banding thresholds shared by API + web so the score color is consistent. */
export function scoreBand(score: number): ReviewScoreBand {
  if (score >= 80) return 'good';
  if (score >= 50) return 'needs-work';
  return 'poor';
}

/**
 * Live progress events streamed from the review agent while it runs, so the
 * modal can show real activity instead of a static spinner. `progress` and
 * `activity` are short status lines; `text` carries streamed reasoning deltas.
 */
export type WorkflowReviewProgressEvent =
  | { type: 'progress'; message: string }
  | { type: 'activity'; label: string }
  | { type: 'text'; delta: string };

/**
 * The full set of server-sent events for a streamed review: progress events
 * during the run, then exactly one terminal `complete` or `error`.
 */
export type WorkflowReviewStreamEvent =
  | WorkflowReviewProgressEvent
  | { type: 'complete'; result: WorkflowReviewResult }
  | { type: 'error'; message: string };

/**
 * Result of a review, returned by `POST /workflows/:id/review` (as the terminal
 * `complete` stream event) and, for the `proposed` case, mirrored onto the
 * created proposal.
 */
export type WorkflowReviewResult =
  | {
      kind: 'proposed';
      proposalId: string;
      summary: string;
      scorecard: ReviewScorecard;
      comments: ReviewComment[];
    }
  | {
      kind: 'comments';
      summary: string;
      scorecard: ReviewScorecard;
      comments: ReviewComment[];
    };
